import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Col, Row, Breadcrumb, Collapse, Space, Tabs, Table, Spin } from "antd";
import { CaretRightOutlined } from "@ant-design/icons";
import { startCase } from "lodash";

import DragUpload from "../Form/DragUpload";
import { getTopupPackage } from "../../utils/topupPackage";

import { PERMISSIONS } from "../../constants/roles";
import { usePermissions } from "../App/PermissionContext";

const columnTypes = {
  prices: [
    "region",
    "state",
    "minWeight",
    "maxWeight",
    "dhl",
    "citylink",
    "jnt",
    "poslaju",
    "ninjavan",
    "teleport",
    "flash",
    "best",
    "lineclear",
    "spx",
    "kex",
    "lex",
  ],
  international: [
    "region",
    "state",
    "minWeight",
    "maxWeight",
    "teleport",
    "sfeconomy",
    "sfexd",
    "ninjavan",
    "aramex",
    "redly",
  ],
  cargo: ["region", "state", "minWeight", "maxWeight", "bestcargo"],
};

const TopupPackageCost = () => {
  const { permissions } = usePermissions();
  const routes = [
    { path: "", breadcrumbName: "App" },
    { path: "/topup-package/cost", breadcrumbName: "Topup Package Cost" },
  ];

  const [isLoading, setIsLoading] = useState(false);
  const [availablePackages, setAvailablePackages] = useState([]);
  const [packageCosts, setPackageCosts] = useState({});
  const [activeTab, setActiveTab] = useState(null);

  const fetchPackageCosts = useCallback(async () => {
    if (!activeTab || packageCosts[activeTab]) {
      return;
    }

    setIsLoading(true);
    const res = await axios.get(`/api/packages/costs`, {
      params: { package: activeTab },
    });

    setPackageCosts((prevPackageCosts) => ({
      ...prevPackageCosts,
      [activeTab]: res.data.data,
    }));

    setIsLoading(false);
  }, [activeTab]);

  const handleUploadSuccess = () => {
    setPackageCosts({});
    setActiveTab("normal");
    fetchPackageCosts();
  };

  useEffect(() => {
    const getAvailablePackages = async () => {
      const response = await axios.get(`/api/topup-packages`);
      const packages = response.data.meta?.packages || [];
      const internationalPackages =
        response.data.meta?.internationalPackages || [];

      setAvailablePackages([...packages, ...internationalPackages]);
      setActiveTab("normal");
    };

    getAvailablePackages();
  }, []);

  useEffect(() => {
    fetchPackageCosts();
  }, [activeTab, fetchPackageCosts]);

  return (
    <div>
      <div className="header">
        <Row>
          <Col span={12}>
            <h2 className="detail-header-text">Topup Package Costs</h2>
          </Col>
          <Col span={12}>
            <Breadcrumb
              style={{ margin: "16px 0", textAlign: "right" }}
              routes={routes}
            />
          </Col>
        </Row>
      </div>

      <Space direction="vertical" size="middle" style={{ display: "flex" }}>
        {permissions?.includes(PERMISSIONS.UPLOAD_PRICING) && (
          <div className="upload">
            <Collapse
              expandIcon={({ isActive }) => (
                <CaretRightOutlined rotate={isActive ? 90 : 0} />
              )}
            >
              <Collapse.Panel header="Upload New Cost" key="upload">
                <DragUpload
                  action={`${process.env.REACT_APP_SERVER_URL}/api/packages/cost`}
                  accept=".xls, .xlsx"
                  method="put"
                  onUploadSuccess={handleUploadSuccess}
                />
              </Collapse.Panel>
            </Collapse>
          </div>
        )}

        <div className="table">
          <Tabs activeKey={activeTab} animated onTabClick={setActiveTab}>
            {availablePackages.length &&
              availablePackages.map((type) => {
                const colsKey = type?.startsWith("international")
                  ? "international"
                  : type?.startsWith("cargo")
                  ? "cargo"
                  : "prices";

                const isInternational = colsKey === "international";
                const isPrices = colsKey === "prices";

                const columns = columnTypes[colsKey].map((columnRaw) => {
                  const col = String(columnRaw).toLowerCase();
                  return {
                    title: <span>{startCase(columnRaw)}</span>,
                    dataIndex: columnRaw,
                    width: isInternational
                      ? col === "region"
                        ? 250
                        : [
                            "state",
                            "sfexd",
                            "redly",
                            "aramex",
                            "teleport",
                            "ninjavan",
                          ].includes(col)
                        ? 80
                        : 110
                      : isPrices
                      ? [
                          "ninjavan",
                          "teleport",
                          "lineclear",
                          "region",
                        ].includes(col)
                        ? 90
                        : [
                            "citylink",
                            "poslaju",
                            "minweight",
                            "maxweight",
                          ].includes(col)
                        ? 80
                        : 70
                      : undefined,
                  };
                });

                return (
                  <Tabs.TabPane tab={getTopupPackage(type).label} key={type}>
                    <Table
                      rowKey="Id"
                      className="table-section"
                      columns={columns}
                      dataSource={packageCosts[type]}
                      loading={isLoading}
                      pagination={{ pageSize: 50 }}
                      scroll={{
                        x: "max-content",
                        y: isPrices ? 600 : undefined,
                      }} // y only where you want it
                      sticky={isPrices ? true : undefined}
                    />
                  </Tabs.TabPane>
                );
              })}
          </Tabs>

          {!availablePackages.length && <Spin size="large" />}
        </div>
      </Space>
    </div>
  );
};

export default TopupPackageCost;
